// Planes de mantenimiento — Taller → Operación → Planes (957).
// Suscripciones vendidas por el POS (factura SRI), venta/renovación desde un
// equipo existente y el catálogo de planes. Sin ventanas modales.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ban, CalendarClock, MessageCircle, Plus, Search, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, fieldCls, money, useSort, SortTh, tallerRpc, QK, PanelIntegrado, useDialogoEnLinea, urlWhatsApp, fmtFecha, TIPOS_EQUIPO, type Catalogo, type ConfigTaller,
} from './shared';
import { TurnoInline } from './caja';

interface PlanDef {
  id: string; nombre: string; descripcion: string | null; tipo_equipo: string | null; producto_id: string; precio: number; meses: number; mantenimientos: number;
  falla_mantenimiento_id: string | null; falla: string | null; descuento_mo_pct: number; prioridad: boolean; activo: boolean; activas: number;
}
interface SuscripcionFila {
  id: string; plan: string; cliente: string; celular: string | null; equipo: string; serial: string | null; inicio: string; fin: string;
  estado: 'activa' | 'cancelada'; monto: number; vigente: boolean; por_vencer: boolean; usados: string | number; mantenimientos: number;
}
interface PlanesData { planes: PlanDef[]; suscripciones: SuscripcionFila[] }
interface EquipoVenta {
  id: string; tipo: string; marca: string | null; modelo: string | null; modelo_id: string | null; serial: string | null; color: string | null;
  partner_id: string; cliente: string; garantia_vigente: boolean;
}
interface ClienteBusc { id: string; nombre: string; identificacion: string; celular: string | null; email: string | null; equipos: number; ordenes: number }
type PagoForm = { forma_pago_id: string; monto: string; referencia: string };
type Pestana = 'suscripciones' | 'vender' | 'planes';

const hoy = () => new Date().toISOString().slice(0, 10);

export function PlanesTaller() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Pestana>('suscripciones');
  const { data, isLoading } = useQuery<PlanesData>({ queryKey: [QK, 'planes'], queryFn: () => tallerRpc('planes_listar'), refetchInterval: 30000 });
  const { data: cfg } = useQuery<ConfigTaller>({ queryKey: [QK, 'config'], queryFn: () => tallerRpc('config_get') });
  const { data: cat } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc('catalogo') });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  return (
    <div className="space-y-3 p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold"><ShieldCheck className="h-5 w-5" /> Planes de mantenimiento</h1>
      <div className="flex flex-wrap gap-1 border-b">
        {([['suscripciones', 'Suscripciones'], ['vender', 'Vender suscripción'], ['planes', 'Planes']] as [Pestana, string][]).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-primary font-medium text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>
      {isLoading && <div className="py-8 text-center text-muted-foreground">Cargando…</div>}
      {!isLoading && tab === 'suscripciones' && <SuscripcionesTab filas={data?.suscripciones ?? []} onOk={inv} />}
      {!isLoading && tab === 'vender' && <VenderTab planes={data?.planes ?? []} cfg={cfg} onOk={inv} />}
      {!isLoading && tab === 'planes' && <PlanesTab planes={data?.planes ?? []} cfg={cfg} cat={cat} onOk={inv} />}
    </div>
  );
}

// ── Suscripciones ────────────────────────────────────────────────────────────
function estadoSusc(s: SuscripcionFila): { label: string; cls: string } {
  if (s.estado === 'cancelada') return { label: 'Cancelada', cls: 'bg-muted text-muted-foreground' };
  if (s.fin < hoy()) return { label: 'Vencida', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' };
  if (s.por_vencer) return { label: 'Por vencer', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' };
  if (s.vigente) return { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' };
  return { label: 'Programada', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' };
}

function SuscripcionesTab({ filas, onOk }: { filas: SuscripcionFila[]; onOk: () => void }) {
  const dlg = useDialogoEnLinea();
  const sort = useSort<SuscripcionFila>(filas, (r, k) => (k === 'plan' ? r.plan : k === 'cliente' ? r.cliente : k === 'equipo' ? r.equipo
    : k === 'fin' ? r.fin : k === 'monto' ? Number(r.monto) : null), 'fin', 'desc');
  const cancelar = useMutation({
    mutationFn: (x: { id: string; motivo: string }) => tallerRpc<{ aviso: string }>('suscripcion_cancelar', { _id: x.id, _motivo: x.motivo }),
    onSuccess: (r) => { toast.success('Suscripción cancelada'); toast.info(r.aviso); onOk(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirCancelar = async (s: SuscripcionFila) => {
    const v = await dlg.pedir(`Cancelar suscripción · ${s.plan}`, [{ key: 'motivo', label: 'Motivo', minimo: 5 }], { aceptar: 'Cancelar suscripción' });
    if (v) cancelar.mutate({ id: s.id, motivo: v.motivo });
  };
  const totalMonto = filas.reduce((a, s) => a + Number(s.monto ?? 0), 0);
  return (
    <div className="space-y-3">
      {dlg.ui}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th>
              <SortTh col="plan" sort={sort}>Plan</SortTh>
              <SortTh col="cliente" sort={sort}>Cliente</SortTh>
              <SortTh col="equipo" sort={sort}>Equipo</SortTh>
              <th className="py-2 pr-2">Vigencia</th>
              <th className="py-2 pr-2">Estado</th>
              <th className="py-2 pr-2">Cupo</th>
              <SortTh col="monto" sort={sort} className="text-right">Monto</SortTh>
              <th className="py-2 pr-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin suscripciones vendidas.</td></tr>}
              {sort.sorted.map((s, i) => {
                const e = estadoSusc(s);
                const renovar = s.estado === 'activa' && (s.por_vencer || s.fin < hoy());
                const mensaje = `Hola ${s.cliente.split(' ')[0]}, su plan de mantenimiento «${s.plan}» ${s.fin < hoy() ? 'venció' : 'vence'} el ${fmtFecha(s.fin)}. Acérquese al taller para renovarlo.`;
                return (
                  <tr key={s.id} className="border-b last:border-0">
                    <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-2 font-medium">{s.plan}</td>
                    <td className="py-2 pr-2">{s.cliente}</td>
                    <td className="py-2 pr-2">{s.equipo}{s.serial && <div className="font-mono text-[11px] text-muted-foreground">{s.serial}</div>}</td>
                    <td className="py-2 pr-2 text-xs">{fmtFecha(s.inicio)} – {fmtFecha(s.fin)}</td>
                    <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span></td>
                    <td className="py-2 pr-2 text-xs">{Number(s.usados)} / {Number(s.mantenimientos)}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{money(Number(s.monto))}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        {renovar && (
                          <a href={urlWhatsApp(s.celular, mensaje)} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline"><MessageCircle className="mr-1 h-3.5 w-3.5" /> Renovar</Button>
                          </a>
                        )}
                        {s.estado === 'activa' && (
                          <Button size="sm" variant="ghost" isLoading={cancelar.isPending} onClick={() => void pedirCancelar(s)}><Ban className="mr-1 h-3.5 w-3.5" /> Cancelar</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={7} className="py-2 pl-3">Total: {filas.length} suscripción(es)</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(totalMonto)}</td>
              <td />
            </tr></tfoot>}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Vender / renovar ─────────────────────────────────────────────────────────
function PagosPlan({ formas, pagos, setPagos }: { formas: ConfigTaller['formas']; pagos: PagoForm[]; setPagos: (p: PagoForm[]) => void }) {
  return (
    <div className="space-y-2">
      {pagos.map((p, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <select className={`${fieldCls} w-56`} value={p.forma_pago_id} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, forma_pago_id: e.target.value } : x)))}>
            <option value="">— Forma de pago —</option>
            {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
          <input type="number" step="0.01" min={0} className={`${fieldCls} w-28 text-right`} placeholder="Monto" value={p.monto} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)))} />
          <input className={`${fieldCls} w-44`} placeholder="Referencia / voucher" value={p.referencia} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, referencia: e.target.value } : x)))} />
          {pagos.length > 1 && <button type="button" aria-label="Quitar" onClick={() => setPagos(pagos.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>}
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setPagos([...pagos, { forma_pago_id: '', monto: '', referencia: '' }])}><Plus className="mr-1 h-4 w-4" /> Otro pago (pago mixto)</Button>
    </div>
  );
}

function VenderTab({ planes, cfg, onOk }: { planes: PlanDef[]; cfg?: ConfigTaller; onOk: () => void }) {
  const [serial, setSerial] = useState('');
  const [qCliente, setQCliente] = useState('');
  const [clienteSel, setClienteSel] = useState<ClienteBusc | null>(null);
  const [equipo, setEquipo] = useState<EquipoVenta | null>(null);
  const [planId, setPlanId] = useState('');
  const formas = (cfg?.formas ?? []).filter((f) => f.id !== cfg?.config?.forma_anticipo_id && f.tipo !== 'credito');
  const [pagos, setPagos] = useState<PagoForm[]>([{ forma_pago_id: formas[0]?.id ?? '', monto: '', referencia: '' }]);

  const { data: clientes = [] } = useQuery<ClienteBusc[]>({
    queryKey: [QK, 'clientes-plan', qCliente], queryFn: () => tallerRpc('clientes_buscar', { _q: qCliente }), enabled: !clienteSel && qCliente.trim().length >= 2,
  });
  const { data: equiposCli = [] } = useQuery<EquipoVenta[]>({
    queryKey: [QK, 'equipos-plan', clienteSel?.id], queryFn: () => tallerRpc('equipos_buscar', { _partner: clienteSel!.id }), enabled: !!clienteSel,
  });

  const buscarSerial = async () => {
    if (!serial.trim()) return;
    try {
      const r = await tallerRpc<EquipoVenta[]>('equipos_buscar', { _serial: serial.trim() });
      if (r[0]) { setEquipo(r[0]); toast.success(`Equipo encontrado: ${r[0].cliente}`); } else { toast.warning('No se encontró un equipo con esa serie'); setEquipo(null); }
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const plan = planes.find((p) => p.id === planId) ?? null;
  const total = plan ? Math.round(plan.precio * 1.15 * 100) / 100 : 0;
  const planesFiltrados = useMemo(() => planes.filter((p) => p.activo && (!equipo || !p.tipo_equipo || p.tipo_equipo === equipo.tipo)), [planes, equipo]);
  const limpiar = (p: PagoForm[]) => p.filter((x) => x.forma_pago_id && Number(x.monto) > 0).map((x) => ({ forma_pago_id: x.forma_pago_id, monto: Number(x.monto), referencia: x.referencia || null }));
  const sumaPagos = limpiar(pagos).reduce((a, x) => a + x.monto, 0);

  const vender = useMutation({
    mutationFn: () => tallerRpc<{ inicio: string; venta: { codigo?: string } | null }>('suscripcion_vender', { _plan: planId, _equipo: equipo!.id, _pagos: limpiar(pagos) }),
    onSuccess: (r) => {
      toast.success(`Plan vendido${r.venta?.codigo ? ` · venta ${r.venta.codigo}` : ''} · inicia ${fmtFecha(r.inicio)}`);
      setEquipo(null); setClienteSel(null); setQCliente(''); setSerial(''); setPlanId(''); setPagos([{ forma_pago_id: formas[0]?.id ?? '', monto: '', referencia: '' }]);
      onOk();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      <Card><CardContent className="space-y-3 p-4 text-sm">
        <div className="font-semibold">1. Elija el equipo</div>
        {!equipo && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Por número de serie</div>
              <div className="flex gap-2">
                <input className={fieldCls} placeholder="Número de serie" value={serial} onChange={(e) => setSerial(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void buscarSerial(); }} />
                <Button size="sm" variant="outline" onClick={() => void buscarSerial()}><Search className="mr-1 h-4 w-4" /> Buscar</Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">O por cliente</div>
              {!clienteSel && (
                <>
                  <input className={fieldCls} placeholder="Nombre, cédula/RUC o celular" value={qCliente} onChange={(e) => setQCliente(e.target.value)} />
                  {clientes.length > 0 && (
                    <div className="max-h-32 overflow-y-auto rounded-md border">
                      {clientes.map((c) => (
                        <button key={c.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-muted" onClick={() => setClienteSel(c)}>
                          {c.nombre} · {c.identificacion}{c.celular ? ` · ${c.celular}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {clienteSel && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between rounded-md border px-2 py-1 text-xs">
                    <span>{clienteSel.nombre} · {clienteSel.identificacion}</span>
                    <button type="button" className="text-muted-foreground underline" onClick={() => { setClienteSel(null); setQCliente(''); }}>Cambiar</button>
                  </div>
                  {equiposCli.length === 0 && <div className="text-xs text-muted-foreground">Este cliente no tiene equipos registrados en el taller.</div>}
                  {equiposCli.map((e) => (
                    <button key={e.id} type="button" className="block w-full rounded-md border px-2 py-1 text-left text-xs hover:bg-muted" onClick={() => setEquipo(e)}>
                      {[e.marca, e.modelo].filter(Boolean).join(' ')} {e.serial ? `· S/N ${e.serial}` : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {equipo && (
          <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <span><strong>{[equipo.marca, equipo.modelo].filter(Boolean).join(' ')}</strong> {equipo.serial ? `· S/N ${equipo.serial}` : ''} · {equipo.cliente}</span>
            <button type="button" className="text-xs text-muted-foreground underline" onClick={() => { setEquipo(null); setPlanId(''); }}>Cambiar equipo</button>
          </div>
        )}
      </CardContent></Card>

      {equipo && (
        <Card><CardContent className="space-y-3 p-4 text-sm">
          <div className="font-semibold">2. Elija el plan</div>
          <Campo label="Plan de mantenimiento">
            <select className={fieldCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">— Elija un plan —</option>
              {planesFiltrados.map((p) => <option key={p.id} value={p.id}>{p.nombre} · {p.meses} meses · {p.mantenimientos} mantenimiento(s) · {money(p.precio)} + IVA</option>)}
            </select>
          </Campo>
          {plan && (
            <div className="rounded-md bg-muted/30 p-2 text-xs text-muted-foreground">
              {plan.descripcion && <div>{plan.descripcion}</div>}
              <div>Incluye {plan.mantenimientos} mantenimiento(s){plan.falla ? ` (${plan.falla})` : ''}{plan.descuento_mo_pct > 0 ? ` · ${plan.descuento_mo_pct}% de descuento en mano de obra` : ''}{plan.prioridad ? ' · prioridad en la cola' : ''}.</div>
              <div>Si el equipo ya tiene un plan vigente, esta compra se registra como renovación: empieza el día siguiente a que termine el actual.</div>
            </div>
          )}
          {plan && (
            <>
              <div className="text-sm">Total con IVA: <strong>{money(total)}</strong></div>
              <PagosPlan formas={formas} pagos={pagos} setPagos={setPagos} />
              <p className="text-xs text-muted-foreground">Se registra como venta en la caja del taller (factura electrónica SRI).</p>
              <TurnoInline motivo="Para vender el plan se necesita el turno de la caja del taller abierto." />
              {sumaPagos + 0.004 < total && <div className="text-xs text-red-600">Faltan {money(total - sumaPagos)} para cubrir el plan.</div>}
              <div className="flex justify-end">
                <Button size="sm" disabled={sumaPagos + 0.004 < total} isLoading={vender.isPending} onClick={() => vender.mutate()}>
                  <ShieldCheck className="mr-1 h-4 w-4" /> Vender plan
                </Button>
              </div>
            </>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

// ── Catálogo de planes ───────────────────────────────────────────────────────
type PlanForm = {
  id: string | null; nombre: string; descripcion: string; tipo_equipo: string; producto_id: string; precio: string; meses: string; mantenimientos: string;
  falla_mantenimiento_id: string; descuento_mo_pct: string; prioridad: boolean; activo: boolean;
};
const planVacio: PlanForm = { id: null, nombre: '', descripcion: '', tipo_equipo: '', producto_id: '', precio: '', meses: '12', mantenimientos: '2', falla_mantenimiento_id: '', descuento_mo_pct: '0', prioridad: true, activo: true };

function PlanesTab({ planes, cfg, cat, onOk }: { planes: PlanDef[]; cfg?: ConfigTaller; cat?: Catalogo; onOk: () => void }) {
  const [form, setForm] = useState<PlanForm | null>(null);
  const sort = useSort<PlanDef>(planes, (r, k) => (k === 'nombre' ? r.nombre : k === 'precio' ? Number(r.precio) : k === 'activas' ? r.activas : null), 'nombre');
  const fallas = (cat?.fallas ?? []).filter((f) => f.activo && (!form?.tipo_equipo || f.tipo === form.tipo_equipo));

  const guardar = useMutation({
    mutationFn: () => tallerRpc('plan_guardar', { _d: {
      id: form!.id, nombre: form!.nombre.trim(), descripcion: form!.descripcion || null, tipo_equipo: form!.tipo_equipo || null, producto_id: form!.producto_id,
      precio: Number(form!.precio), meses: Number(form!.meses), mantenimientos: Number(form!.mantenimientos), falla_mantenimiento_id: form!.falla_mantenimiento_id || null,
      descuento_mo_pct: Number(form!.descuento_mo_pct), prioridad: form!.prioridad, activo: form!.activo,
    } }),
    onSuccess: () => { toast.success('Plan guardado'); setForm(null); onOk(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const editar = (p: PlanDef) => setForm({
    id: p.id, nombre: p.nombre, descripcion: p.descripcion ?? '', tipo_equipo: p.tipo_equipo ?? '', producto_id: p.producto_id, precio: String(p.precio),
    meses: String(p.meses), mantenimientos: String(p.mantenimientos), falla_mantenimiento_id: p.falla_mantenimiento_id ?? '', descuento_mo_pct: String(p.descuento_mo_pct),
    prioridad: p.prioridad, activo: p.activo,
  });
  const valido = !!form && form.nombre.trim().length >= 3 && !!form.producto_id && Number(form.precio) > 0;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setForm({ ...planVacio })}><Plus className="mr-1 h-4 w-4" /> Nuevo plan</Button>
      </div>

      {form && (
        <PanelIntegrado title={form.id ? 'Editar plan' : 'Nuevo plan'} onClose={() => setForm(null)} wide>
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <Campo label="Nombre"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Tipo de equipo (opcional)">
              <select className={fieldCls} value={form.tipo_equipo} onChange={(e) => setForm({ ...form, tipo_equipo: e.target.value })}>
                <option value="">— Todos —</option>
                {TIPOS_EQUIPO.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Campo>
            <Campo label="Descripción" ><textarea className={`${fieldCls} h-16 sm:col-span-2`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Campo label="Servicio con el que se factura">
              <select className={fieldCls} value={form.producto_id} onChange={(e) => setForm({ ...form, producto_id: e.target.value })}>
                <option value="">— Elija el servicio —</option>
                {(cfg?.servicios ?? []).map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Precio sin IVA"><input type="number" step="0.01" min={0} className={fieldCls} value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })} /></Campo>
            <Campo label="Duración (meses)"><input type="number" min={1} max={36} className={fieldCls} value={form.meses} onChange={(e) => setForm({ ...form, meses: e.target.value })} /></Campo>
            <Campo label="Mantenimientos incluidos"><input type="number" min={0} className={fieldCls} value={form.mantenimientos} onChange={(e) => setForm({ ...form, mantenimientos: e.target.value })} /></Campo>
            <Campo label="Falla de mantenimiento (sin costo, consume cupo)">
              <select className={fieldCls} value={form.falla_mantenimiento_id} onChange={(e) => setForm({ ...form, falla_mantenimiento_id: e.target.value })}>
                <option value="">— Ninguna —</option>
                {fallas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="% descuento en mano de obra"><input type="number" min={0} max={100} className={fieldCls} value={form.descuento_mo_pct} onChange={(e) => setForm({ ...form, descuento_mo_pct: e.target.value })} /></Campo>
            <label className="flex items-center gap-2 pt-5 text-sm"><input type="checkbox" checked={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.checked })} /> Prioridad en la cola</label>
            {form.id && <label className="flex items-center gap-2 pt-5 text-sm"><input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} /> Activo</label>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setForm(null)}>Cancelar</Button>
            <Button size="sm" disabled={!valido} isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
          </div>
        </PanelIntegrado>
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th>
              <SortTh col="nombre" sort={sort}>Plan</SortTh>
              <th className="py-2 pr-2">Tipo</th>
              <SortTh col="precio" sort={sort} className="text-right">Precio s/IVA</SortTh>
              <th className="py-2 pr-2">Meses</th>
              <th className="py-2 pr-2">Mantenimientos</th>
              <th className="py-2 pr-2">Descuento M.O.</th>
              <SortTh col="activas" sort={sort}>Vigentes</SortTh>
              <th className="py-2 pr-2">Activo</th>
              <th className="py-2 pr-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin planes registrados.</td></tr>}
              {sort.sorted.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-2 font-medium">{p.nombre}{p.prioridad && <CalendarClock className="ml-1 inline h-3.5 w-3.5 text-amber-500" aria-label="Con prioridad" />}</td>
                  <td className="py-2 pr-2 text-xs">{TIPOS_EQUIPO.find(([k]) => k === p.tipo_equipo)?.[1] ?? 'Todos'}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{money(Number(p.precio))}</td>
                  <td className="py-2 pr-2">{p.meses}</td>
                  <td className="py-2 pr-2">{p.mantenimientos}{p.falla ? ` (${p.falla})` : ''}</td>
                  <td className="py-2 pr-2">{p.descuento_mo_pct}%</td>
                  <td className="py-2 pr-2">{p.activas}</td>
                  <td className="py-2 pr-2">{p.activo ? 'Sí' : 'No'}</td>
                  <td className="py-2 pr-3 text-right"><Button size="sm" variant="outline" onClick={() => editar(p)}><Wrench className="mr-1 h-3.5 w-3.5" /> Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
